/**
 * domainPalette.ts
 *
 * Single source of truth for domain colour mapping across all map components.
 *
 * Family-clustered palette per agent3 visual spec §1.3:
 *   Structure & Building  — indigo / violet band (239–265°)
 *   Services — Wet        — cyan / teal band (175–195°)
 *   Services — Electrical — blue band (205–225°)
 *   People & Product      — warm band (red / pink / orange / amber / fuchsia)
 *
 * The warm family is reserved for human-safety-adjacent domains so they visually
 * advance against the cool dark field — a triage cue, not decoration.
 * Fire-safety stays --danger red always.
 *
 * DEFAULT_DOMAIN_COLOR → --ink-5 #52525b (grey = "outside the known system",
 * consistent with Agent 2's Absent-node treatment).
 */

// ── Family: Structure & Building ─────────────────────────────────────────
// Shares the app accent hue (indigo) so structural standards feel "native"
// to the query spine. Variation within the family: hue ±15°, one lightness step.

// ── Family: Services — Wet ────────────────────────────────────────────────
// Cyan/teal for water-related work; physically adjacent trades share adjacent hues.

// ── Family: Services — Electrical ────────────────────────────────────────
// Blue band; cooler than structural, warmer than cyan. Three standards, three steps.

// ── Family: People & Product ──────────────────────────────────────────────
// Warm hues (red → pink → orange → amber → fuchsia). These pop against the cool
// dark field; fire-safety red matches --danger for alarm-level recognition.

export const DOMAIN_COLORS: Record<string, string> = {
  // Structure & Building — indigo/violet family
  'ncc-compliance':    '#6366F1',   // indigo — primary accent, the "spine" colour
  'structural':        '#8B5CF6',   // violet
  'structural-compliance': '#8B5CF6', // alias
  'energy-efficiency': '#7C6FF0',   // blue-violet bridge

  // Services — Wet — cyan/teal family
  'plumbing':          '#06B6D4',   // cyan
  'plumbing-compliance': '#06B6D4', // alias
  'waterproofing':     '#22D3EE',   // sky cyan (lighter, distinguishable from plumbing)
  'waterproofing-compliance': '#22D3EE', // alias
  'gas':               '#14B8A6',   // teal (warm-shifted to distinguish from wet work)

  // Services — Electrical — blue family
  'electrical':        '#3B82F6',   // blue
  'electrical-compliance': '#3B82F6', // alias
  'eess':              '#0EA5E9',   // sky blue (lighter)
  'eess-registration': '#0EA5E9',   // alias
  'emc':               '#60A5FA',   // lighter blue
  'emc-compliance':    '#60A5FA',   // alias

  // People & Product — warm family (life-safety pops against the cool field)
  'fire-safety':       '#EF4444',   // red — matches --danger; always alarm-red
  'fire':              '#EF4444',   // alias
  'accessibility':     '#EC4899',   // pink
  'accessibility-compliance': '#EC4899', // alias
  'dda-compliance':    '#EC4899',   // DDA = accessibility
  'product-safety':    '#F97316',   // orange
  'acl-consumer-law':  '#F59E0B',   // amber
  'glazing':           '#D946EF',   // fuchsia
  'glazing-compliance': '#D946EF',  // alias
  'uk-eu-compliance':  '#6B7280',   // grey — meta/foreign, no family
  'hvac':              '#10B981',   // emerald (mechanical services, not in spec families)
};

/** Fallback for unknown/unmapped domains. Matches --ink-5 (Absent-node grey). */
export const DEFAULT_DOMAIN_COLOR = '#52525b';

/**
 * Return the domain colour for a given domain slug.
 * Case-insensitive; falls back to DEFAULT_DOMAIN_COLOR for unknowns.
 */
export function getDomainColor(domain: string): string {
  if (!domain) return DEFAULT_DOMAIN_COLOR;
  return DOMAIN_COLORS[domain.toLowerCase()] ?? DEFAULT_DOMAIN_COLOR;
}

/**
 * Return the family name for a domain, for layout/gravity grouping.
 * Returns 'structure' | 'wet' | 'electrical' | 'people' | 'meta' | 'unknown'.
 */
export type DomainFamily = 'structure' | 'wet' | 'electrical' | 'people' | 'meta' | 'unknown';

const FAMILY_MAP: Record<string, DomainFamily> = {
  'ncc-compliance':    'structure',
  'structural':        'structure',
  'structural-compliance': 'structure',
  'energy-efficiency': 'structure',
  'plumbing':          'wet',
  'plumbing-compliance': 'wet',
  'waterproofing':     'wet',
  'waterproofing-compliance': 'wet',
  'gas':               'wet',
  'electrical':        'electrical',
  'electrical-compliance': 'electrical',
  'eess':              'electrical',
  'eess-registration': 'electrical',
  'emc':               'electrical',
  'emc-compliance':    'electrical',
  'fire-safety':       'people',
  'fire':              'people',
  'accessibility':     'people',
  'accessibility-compliance': 'people',
  'dda-compliance':    'people',
  'product-safety':    'people',
  'acl-consumer-law':  'people',
  'glazing':           'people',
  'glazing-compliance':'people',
  'uk-eu-compliance':  'meta',
  'hvac':              'unknown',
};

export function getDomainFamily(domain: string): DomainFamily {
  if (!domain) return 'unknown';
  return FAMILY_MAP[domain.toLowerCase()] ?? 'unknown';
}
